import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { CheckIcon, ChevronsUpDown } from 'lucide-react';
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import { useTranslations } from 'next-intl';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn, detectCountryFromLocale } from '@/lib/utils';
type PhoneInputProps = Omit<React.ComponentProps<'input'>, 'onChange' | 'value' | 'ref'> & Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange'> & {
  onChange?: (value: RPNInput.Value) => void;
};
const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> = React.forwardRef<React.ComponentRef<typeof RPNInput.default>, PhoneInputProps>((t0, ref) => {
  const $ = _c(22);
  let ariaInvalid;
  let className;
  let defaultCountry;
  let onChange;
  let props;
  let value;
  if ($[0] !== t0) {
    ({
      className,
      onChange,
      value,
      defaultCountry,
      "aria-invalid": ariaInvalid,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = ariaInvalid;
    $[2] = className;
    $[3] = defaultCountry;
    $[4] = onChange;
    $[5] = props;
    $[6] = value;
  } else {
    ariaInvalid = $[1];
    className = $[2];
    defaultCountry = $[3];
    onChange = $[4];
    props = $[5];
    value = $[6];
  }
  const [autoCountry] = React.useState(_temp);
  let t1;
  if ($[7] !== ariaInvalid) {
    t1 = csProps => <CountrySelect {...csProps} isInvalid={!!ariaInvalid} />;
    $[7] = ariaInvalid;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const CountrySelectWithInvalid = t1;
  let t2;
  if ($[9] !== className) {
    t2 = cn("flex", className);
    $[9] = className;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const t3 = defaultCountry ?? autoCountry;
  const t4 = value || undefined;
  let t5;
  if ($[11] !== onChange) {
    t5 = value_0 => onChange?.(value_0 || "" as RPNInput.Value);
    $[11] = onChange;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== CountrySelectWithInvalid || $[14] !== ariaInvalid || $[15] !== props || $[16] !== ref || $[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== t5) {
    t6 = <RPNInput.default ref={ref} className={t2} flagComponent={FlagComponent} countrySelectComponent={CountrySelectWithInvalid} inputComponent={InputComponent} smartCaret={false} defaultCountry={t3} value={t4} onChange={t5} aria-invalid={ariaInvalid} {...props} />;
    $[13] = CountrySelectWithInvalid;
    $[14] = ariaInvalid;
    $[15] = props;
    $[16] = ref;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
});
PhoneInput.displayName = 'PhoneInput';
const InputComponent = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("rounded-s-none rounded-e-lg", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <Input className={t1} {...props} ref={ref} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
InputComponent.displayName = 'InputComponent';
type CountryEntry = {
  label: string;
  value: RPNInput.Country | undefined;
};
type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: CountryEntry[];
  onChange: (country: RPNInput.Country) => void;
  isInvalid?: boolean;
};
const CountrySelect = t0 => {
  const $ = _c(39);
  const {
    disabled,
    value: selectedCountry,
    options: countryList,
    onChange,
    isInvalid
  } = t0;
  const t = useTranslations();
  const scrollAreaRef = React.useRef(null);
  const [searchValue, setSearchValue] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = open => {
      setIsOpen(open);
      if (open) {
        setSearchValue("");
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = isInvalid && "border-destructive ring-destructive/20 ring-[3px]";
  let t3;
  if ($[1] !== t2) {
    t3 = cn("flex h-9 gap-1 rounded-s-lg rounded-e-none border-r-0 px-3 focus:z-10", t2);
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== selectedCountry) {
    t4 = <FlagComponent country={selectedCountry} countryName={selectedCountry} />;
    $[3] = selectedCountry;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = disabled ? "hidden" : "opacity-100";
  let t6;
  if ($[5] !== t5) {
    t6 = cn("-mr-2 size-4 opacity-50", t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = <ChevronsUpDown className={t6} />;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== disabled || $[10] !== t3 || $[11] !== t4 || $[12] !== t7) {
    t8 = <PopoverTrigger asChild={true}><Button type="button" variant="outline" className={t3} disabled={disabled}>{t4}{t7}</Button></PopoverTrigger>;
    $[9] = disabled;
    $[10] = t3;
    $[11] = t4;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = value => {
      setSearchValue(value);
      setTimeout(() => {
        if (scrollAreaRef.current) {
          const viewportElement = scrollAreaRef.current.querySelector("[data-radix-scroll-area-viewport]");
          if (viewportElement) {
            viewportElement.scrollTop = 0;
          }
        }
      }, 0);
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = t("search") + "...";
  let t11;
  if ($[15] !== searchValue || $[16] !== t10) {
    t11 = <CommandInput value={searchValue} onValueChange={t9} placeholder={t10} />;
    $[15] = searchValue;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const t12 = t("not_found");
  let t13;
  if ($[18] !== t12) {
    t13 = <CommandEmpty>{t12}</CommandEmpty>;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== countryList || $[21] !== onChange || $[22] !== selectedCountry) {
    let t15;
    if ($[24] !== onChange || $[25] !== selectedCountry) {
      t15 = t16 => {
        const {
          value: value_0,
          label
        } = t16;
        return value_0 ? <CountrySelectOption key={value_0} country={value_0} countryName={label} selectedCountry={selectedCountry} onChange={onChange} onSelectComplete={() => setIsOpen(false)} /> : null;
      };
      $[24] = onChange;
      $[25] = selectedCountry;
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    t14 = countryList.map(t15);
    $[20] = countryList;
    $[21] = onChange;
    $[22] = selectedCountry;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[27] !== t14) {
    t15 = <CommandGroup>{t14}</CommandGroup>;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t13 || $[30] !== t15) {
    t16 = <CommandList><ScrollArea ref={scrollAreaRef} className="h-72">{t13}{t15}</ScrollArea></CommandList>;
    $[29] = t13;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t11 || $[33] !== t16) {
    t17 = <PopoverContent className="w-75 p-0"><Command>{t11}{t16}</Command></PopoverContent>;
    $[32] = t11;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== isOpen || $[36] !== t17 || $[37] !== t8) {
    t18 = <Popover open={isOpen} onOpenChange={t1}>{t8}{t17}</Popover>;
    $[35] = isOpen;
    $[36] = t17;
    $[37] = t8;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  return t18;
};
interface CountrySelectOptionProps extends RPNInput.FlagProps {
  selectedCountry: RPNInput.Country;
  onChange: (country: RPNInput.Country) => void;
  onSelectComplete: () => void;
}
const CountrySelectOption = t0 => {
  const $ = _c(21);
  const {
    country,
    countryName,
    selectedCountry,
    onChange,
    onSelectComplete
  } = t0;
  let t1;
  if ($[0] !== country || $[1] !== onChange || $[2] !== onSelectComplete) {
    t1 = () => {
      onChange(country);
      onSelectComplete();
    };
    $[0] = country;
    $[1] = onChange;
    $[2] = onSelectComplete;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleSelect = t1;
  let t2;
  if ($[4] !== country || $[5] !== countryName) {
    t2 = <FlagComponent country={country} countryName={countryName} />;
    $[4] = country;
    $[5] = countryName;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== countryName) {
    t3 = <span className="flex-1 text-sm">{countryName}</span>;
    $[7] = countryName;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== country) {
    t4 = RPNInput.getCountryCallingCode(country);
    $[9] = country;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = `+${t4}`;
  let t6;
  if ($[11] !== t5) {
    t6 = <span className="text-foreground/50 text-sm">{t5}</span>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `ml-auto size-4 ${country === selectedCountry ? "opacity-100" : "opacity-0"}`;
  let t8;
  if ($[13] !== t7) {
    t8 = <CheckIcon className={t7} />;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== handleSelect || $[16] !== t2 || $[17] !== t3 || $[18] !== t6 || $[19] !== t8) {
    t9 = <CommandItem className="gap-2" onSelect={handleSelect}>{t2}{t3}{t6}{t8}</CommandItem>;
    $[15] = handleSelect;
    $[16] = t2;
    $[17] = t3;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
const FlagComponent = t0 => {
  const $ = _c(5);
  const {
    country,
    countryName
  } = t0;
  const Flag = flags[country];
  let t1;
  if ($[0] !== Flag || $[1] !== countryName) {
    t1 = Flag && <Flag title={countryName} />;
    $[0] = Flag;
    $[1] = countryName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <span className="bg-foreground/20 flex h-4 w-6 overflow-hidden rounded-sm [&_svg:not([class*='size-'])]:size-full">{t1}</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export { PhoneInput };
function _temp() {
  return detectCountryFromLocale();
}